import { z } from 'zod';
import { userRoleSchema } from './user';
import { planTierSchema, subscriptionStatusSchema, subscriptionSourceSchema } from './subscription';

// ==================== Admin User Management ====================

// Request: Create a new user (admin only)
export const createUserRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  name: z.string().min(1).max(100),
  role: userRoleSchema.optional().default('user'),
});
export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;

// Request: Update a user (admin only)
export const updateUserRequestSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  role: userRoleSchema.optional(),
});
export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

// Response: Create user
export const createUserResponseSchema = z.object({
  success: z.boolean(),
  userId: z.string().uuid(),
});
export type CreateUserResponse = z.infer<typeof createUserResponseSchema>;

// ==================== License Management ====================

// Request: Activate a manual license
// Note: expiresAt is required for Pro plans, optional for Starter
export const activateLicenseRequestSchema = z.object({
  userId: z.string().uuid(),
  planTier: planTierSchema,
  expiresAt: z.string().datetime().optional(),
  resellerName: z.string().optional(),
  resellerNotes: z.string().optional(),
});
export type ActivateLicenseRequest = z.infer<typeof activateLicenseRequestSchema>;

// Request: Extend an existing license
export const extendLicenseRequestSchema = z.object({
  userId: z.string().uuid(),
  newExpiresAt: z.string().datetime(),
  notes: z.string().optional(),
});
export type ExtendLicenseRequest = z.infer<typeof extendLicenseRequestSchema>;

// Response: License operation
export const licenseOperationResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});
export type LicenseOperationResponse = z.infer<typeof licenseOperationResponseSchema>;

// ==================== Admin List Views ====================

// Admin subscription info (subset of full subscription)
export const adminSubscriptionInfoSchema = z.object({
  planTier: planTierSchema,
  status: subscriptionStatusSchema,
  source: subscriptionSourceSchema,
  manualExpiresAt: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  resellerName: z.string().nullable(),
});
export type AdminSubscriptionInfo = z.infer<typeof adminSubscriptionInfoSchema>;

// Admin user list item
export const adminUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  role: userRoleSchema,
  createdAt: z.string().datetime(),
  subscription: adminSubscriptionInfoSchema.nullable(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

// Response: List users
export const listUsersResponseSchema = z.object({
  users: z.array(adminUserSchema),
});
export type ListUsersResponse = z.infer<typeof listUsersResponseSchema>;

// Response: Get user detail
export const getUserDetailResponseSchema = z.object({
  user: adminUserSchema,
});
export type GetUserDetailResponse = z.infer<typeof getUserDetailResponseSchema>;
